import React from 'react';
import { useParams } from 'react-router-dom';

import CountdownTimer from '@dashboard/components/CountdownTimer';
import { FWorkflowStepFragment, SleepTimeUnits, WorkflowStepStatus } from '@dashboard/generated/wp/platform';
import { AccessTimeOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';

import { NumberFormatter } from '@waypoint/react-formatters';

import CancelWorkflowRunButton from '../createWorkflowRunCommands/CancelWorkflowRunButton';
import WakeupWorkflowRunButton from '../createWorkflowRunCommands/WakeupWorkflowRunButton';
import WorkflowCard from '../WorkflowCard';

import CardCaption from './CardCaption';
import TitleIcon from './TitleIcon';

type Props = {
  step: FWorkflowStepFragment;
};
export default function Sleep({ step }: Props) {
  const id = useParams<{ id: string }>().id!;

  const { timeAmount, timeUnit } = step.definition.sleep!;

  const renderBody = () => {
    if (step.status === WorkflowStepStatus.Processing) {
      const date = new Date(step.result!.sleep!.wakeupScheduledAt);
      return <CountdownTimer goalDate={date} />;
    }
    return null;
  };

  const renderActions = () => {
    switch (step.status) {
      case WorkflowStepStatus.Canceled:
      case WorkflowStepStatus.Disabled:
      case WorkflowStepStatus.Failed:
      case WorkflowStepStatus.Succeeded:
        return null;
      case WorkflowStepStatus.Pending:
      case WorkflowStepStatus.Processing:
        return (
          <Stack spacing={2} direction="row" justifyContent="end">
            <CancelWorkflowRunButton workflowRunId={id} />
            <WakeupWorkflowRunButton workflowRunId={id} />
          </Stack>
        );
    }
  };

  return (
    <WorkflowCard
      titleIcon={<TitleIcon step={step} />}
      titleText="Delay"
      labelIcon={<AccessTimeOutlined fontSize="small" />}
      labelText={
        <>
          <NumberFormatter value={timeAmount} /> {renderTimeUnit(timeAmount, timeUnit)}
        </>
      }
      caption={<CardCaption step={step} />}
      actions={renderActions()}
    >
      {renderBody()}
    </WorkflowCard>
  );
}

function renderTimeUnit(timeAmount: number, timeUnit: SleepTimeUnits): string {
  switch (timeUnit) {
    case SleepTimeUnits.Seconds:
      return timeAmount === 1 ? 'second' : 'seconds';
    case SleepTimeUnits.Minutes:
      return timeAmount === 1 ? 'minute' : 'minutes';
    case SleepTimeUnits.Hours:
      return timeAmount === 1 ? 'hour' : 'hours';
    case SleepTimeUnits.Days:
      return timeAmount === 1 ? 'day' : 'days';
    case SleepTimeUnits.Weeks:
      return timeAmount === 1 ? 'week' : 'weeks';
  }
}
